import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"

import { useClient } from "api"

type RecaptchaConfiguration = {
  enabled: boolean
  siteKey: string
}

type RecaptchaEnterprise = {
  ready: (callback: () => void) => void
  execute: (siteKey: string, options: { action: string }) => Promise<string>
}

declare global {
  interface Window {
    grecaptcha?: { enterprise?: RecaptchaEnterprise }
  }
}

export type RecaptchaAction = "onboarding_step" | "onboarding_submit"

export type OnboardingRecaptchaHandle = {
  getToken: (action: RecaptchaAction) => Promise<string | undefined>
}

export class RecaptchaError extends Error {}

const SCRIPT_ID = "onboarding-recaptcha-enterprise"
const LOAD_TIMEOUT_MS = 15000
const EXECUTE_TIMEOUT_MS = 30000
const LOAD_FAILED_MESSAGE = "Human verification failed to load. Please check your connection and try again."

let pendingLoad: { siteKey: string; promise: Promise<RecaptchaEnterprise> } | undefined

const removeProvider = () => {
  document.getElementById(SCRIPT_ID)?.remove()
  // Google's child scripts may still be initializing; preserve their shared globals.
}

const loadProviderAttempt = (siteKey: string) => {
  removeProvider()
  return new Promise<RecaptchaEnterprise>((resolve, reject) => {
    const script = document.createElement("script")
    let settled = false
    const fail = () => {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      script.onload = null
      script.onerror = null
      removeProvider()
      reject(new RecaptchaError(LOAD_FAILED_MESSAGE))
    }
    const timeout = setTimeout(fail, LOAD_TIMEOUT_MS)

    script.id = SCRIPT_ID
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`
    script.async = true
    script.onerror = fail
    script.onload = () => {
      const enterprise = window.grecaptcha?.enterprise
      if (typeof enterprise?.ready !== "function") {
        fail()
        return
      }
      enterprise.ready(() => {
        if (settled) return
        const readyEnterprise = window.grecaptcha?.enterprise
        if (typeof readyEnterprise?.execute !== "function") {
          fail()
          return
        }
        settled = true
        clearTimeout(timeout)
        script.onload = null
        script.onerror = null
        resolve(readyEnterprise)
      })
    }
    document.head.appendChild(script)
  })
}

const loadRecaptchaEnterprise = (siteKey: string) => {
  if (pendingLoad?.siteKey === siteKey) return pendingLoad.promise

  const promise = loadProviderAttempt(siteKey).catch(() => loadProviderAttempt(siteKey))

  const load = { siteKey, promise }
  pendingLoad = load
  promise.catch(() => {
    if (pendingLoad === load) pendingLoad = undefined
  })
  return promise
}

type ProviderStatus = "loading" | "ready" | "failed"

export const OnboardingRecaptcha = forwardRef<OnboardingRecaptchaHandle>((_props, ref) => {
  const { read } = useClient()
  const provider = useRef<RecaptchaEnterprise>()
  const [status, setStatus] = useState<ProviderStatus>("loading")
  const [attempt, setAttempt] = useState(0)
  const [error, setError] = useState<string>()
  const {
    data: configuration,
    isError,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["onboarding-recaptcha"],
    queryFn: () => read<RecaptchaConfiguration>("Patients/recaptcha"),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const enabled = configuration?.enabled === true
  const siteKey = configuration?.siteKey?.trim()

  useEffect(() => {
    if (!enabled || !siteKey) return

    let active = true
    provider.current = undefined
    setStatus("loading")
    loadRecaptchaEnterprise(siteKey).then(
      (enterprise) => {
        if (!active) return
        provider.current = enterprise
        setStatus("ready")
      },
      () => {
        if (active) setStatus("failed")
      },
    )
    return () => {
      active = false
    }
  }, [enabled, siteKey, attempt])

  const reloadProvider = () => {
    provider.current = undefined
    setStatus("loading")
    setError(undefined)
    setAttempt((current) => current + 1)
  }

  useImperativeHandle(ref, () => ({
    getToken: async (action) => {
      if (!configuration || isError) throw new RecaptchaError("Human verification is unavailable. Please try again.")
      if (!configuration.enabled) return undefined
      if (status === "failed") throw new RecaptchaError(LOAD_FAILED_MESSAGE)
      if (!siteKey || status !== "ready" || !provider.current)
        throw new RecaptchaError("Human verification is not ready. Please try again.")

      setError(undefined)
      let timeout: ReturnType<typeof setTimeout> | undefined
      try {
        const token = await Promise.race([
          provider.current.execute(siteKey, { action }),
          new Promise<never>((_resolve, reject) => {
            timeout = setTimeout(() => reject(new Error("Human verification timed out.")), EXECUTE_TIMEOUT_MS)
          }),
        ])
        if (!token) throw new Error("Human verification returned no token.")
        return token
      } catch {
        const message = "Unable to verify you. Please try again."
        setError(message)
        throw new RecaptchaError(message)
      } finally {
        clearTimeout(timeout)
      }
    },
  }))

  if (configuration?.enabled === false) return null

  const loading = isPending || (enabled && !!siteKey && status === "loading")

  return (
    <div className="px-8 pb-6 empty:hidden" aria-busy={loading}>
      {loading && (
        <p role="status" className="text-sm text-gray-400">
          Loading human verification...
        </p>
      )}
      {(isError || (configuration && !siteKey)) && (
        <div role="alert" className="text-yellow-700 text-sm">
          <p>Human verification is unavailable.</p>
          <button type="button" className="underline border-none ring-0" onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      )}
      {enabled && siteKey && status === "failed" && (
        <div role="alert" className="text-red-700 text-sm">
          <p>{LOAD_FAILED_MESSAGE}</p>
          <button type="button" className="underline border-none ring-0" onClick={reloadProvider}>
            Try again
          </button>
        </div>
      )}
      {error && status === "ready" && (
        <small role="alert" className="text-sm text-red-700">
          {error}
        </small>
      )}
    </div>
  )
})

OnboardingRecaptcha.displayName = "OnboardingRecaptcha"
